import { ReactElement } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useLanguage } from 'context/AppContext';
import { DeliveriesRequestFilters } from 'api/models/Deliveries';
import Button from 'components/Button';
import { FilterId } from '../types';
import { format, isToday, isTomorrow } from 'date-fns';
import { OrdersFilters } from 'api/models/Orders';
import { TurnaroundListFilters } from 'api/models/TurnaroundList';
import useStatusTranslations from 'hooks/useStatusTranslations';

export type SupportedFilterNames = keyof (DeliveriesRequestFilters & OrdersFilters & TurnaroundListFilters);

export type FilterNamesMap = Partial<Record<FilterId, SupportedFilterNames>>;

const IGNORED_FILTERS = ['search', 'limit', 'offset', 'historical', 'sort_order', 'sort_by'];

const FiltersSelected = <T extends DeliveriesRequestFilters | OrdersFilters | TurnaroundListFilters>({
  filters,
  enabledFilters,
  setFilters,
}: {
  filters: T;
  enabledFilters: FilterNamesMap;
  setFilters: (filters: T) => void;
}) => {
  const [lang] = useLanguage();
  const tableLang = lang.DeliveryListPage.table;
  const filtersLang = lang.DeliveryListPage.filterModal;
  const statusLang = useStatusTranslations();

  // find the filters with values
  const selected = Object.entries(filters).filter(([k, v]) => !IGNORED_FILTERS.includes(k) && v);

  if (!selected.length) return null;

  // translate the name
  return (
    <>
      {Object.entries(enabledFilters).reduce<ReactElement[]>((a, [filterId, k]) => {
        const v = filters[k];

        if (v) {
          const translation = tableLang[filterId];
          const name = 'string' == typeof translation ? translation : translation.label || filterId;
          let value = 'string' == typeof v ? v : v.label;

          if (filterId.includes('Date')) {
            const date = new Date(value);
            value = isToday(date)
              ? filtersLang.today
              : isTomorrow(date)
              ? filtersLang.tomorrow
              : format(date, 'dd-MM-yy');
          }

          // if the filter is status, parse the value into a label
          else if (filterId == 'status') {
            value = statusLang[value] || value;
          }

          const label = `${name}: ${value}`;

          a.push(
            <Button
              key={filterId}
              label={
                <span
                  style={{
                    maxWidth: '172px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {label}
                </span>
              }
              onClick={() => {
                const newFilters = { ...filters };

                // delete the filter
                delete newFilters[k];

                setFilters(newFilters);
              }}
              endIcon={<CloseIcon />}
            />,
          );
        }

        return a;
      }, [])}
    </>
  );
};

export default FiltersSelected;
